import { DragControls } from "three/examples/jsm/controls/DragControls.js";
import { Line2 } from "three/examples/jsm/lines/Line2.js";
import { LineMaterial } from "three/examples/jsm/lines/LineMaterial.js";
import { CSS2DObject } from "three/examples/jsm/renderers/CSS2DRenderer.js";
import { LoadCaseType, LoadType } from "./enums";
import { AccelerationItem, FrequencyTableEntry } from "./interfaces";
import { SessionService } from "../services/session.service";
import { ArrowHelper } from '../engine/addons/load-helpers/arrowhelper';
import { Object3D } from "three";

export class LoadItem {

    constructor(
        public id: string,
        public partId: any,
        public partFaces: string[] = [],
        public type: LoadType = LoadType.FORCE,
        // FORCE
        public remotePoint: number[] = [0, 0, 0], // XYZ
        public forceComponent: number[] = [0, -1, 0], // XYZ
        public momentComponent: number[] = [0, 0, 0], // XYZ 
        // PRESSURE
        public pressureMagnitude: number = 0.1,
        // ACCELERATION
        public nonStructuralMass: number = 0,
        public centerOfMass: number[] = [0, 0, 0],
        public checked = true
    ) {

    }

    static fromJSON(data: any) {
        return new LoadItem(
            data.id,
            data.partId,
            data.partFaces,
            data.type,
            data.remotePoint,
            data.forceComponent,
            data.momentComponent,
            data.pressureMagnitude,
            data.nonStructuralMass,
            data.centerOfMass
        )
    }

    toJSON() {
        return {
            id: this.id,
            partId: this.partId,
            partFaces: this.partFaces,
            type: this.type,
            // FORCE
            remotePoint: this.remotePoint, // XYZ
            forceComponent: this.forceComponent, // XYZ
            momentComponent: this.momentComponent, // XYZ 
            // PRESSURE
            pressureMagnitude: this.pressureMagnitude,
            // ACCELERATION
            nonStructuralMass: this.nonStructuralMass,
            centerOfMass: this.centerOfMass
        }
    }

    static parse() { }

    clone() { }

    getPartName(session: SessionService) {
        const item = session.engine?.objects.find(obj => obj.data.id === this.partId);
        if (item) {
            return item.data.name;
        }
        return '';
    }

}

export class BoundaryConditionItem {

    constructor(
        public id: string,
        public partId: any,
        public partFaces: string[] = [],
        public fixedX: boolean = true,
        public fixedY: boolean = true,
        public fixedZ: boolean = true,
        public rotationAlongAxis: boolean = false,
        public rotationAxisX: number = 0,
        public rotationAxisY: number = 0,
        public rotationAxisZ: number = 0
    ) {

    }

    static fromJSON(data: any, newId?: string) {
        if (newId) {
            return new BoundaryConditionItem(
                newId,
                data.partId,
                data.partFaces,
                data.fixedX,
                data.fixedY,
                data.fixedZ,
                data.rotationAlongAxis,
                data.rotationAxisX,
                data.rotationAxisY,
                data.rotationAxisZ
            );
        }
        return new BoundaryConditionItem(
            data.id,
            data.partId,
            data.partFaces,
            data.fixedX,
            data.fixedY,
            data.fixedZ,
            data.rotationAlongAxis,
            data.rotationAxisX,
            data.rotationAxisY,
            data.rotationAxisZ
        );
    }

    toJSON() {
        return {
            id: this.id,
            partId: this.partId,
            partFaces: this.partFaces,
            fixedX: this.fixedX,
            fixedY: this.fixedY,
            fixedZ: this.fixedZ,
            rotationAlongAxis: this.rotationAlongAxis,
            rotationAxisX: this.rotationAxisX,
            rotationAxisY: this.rotationAxisY,
            rotationAxisZ: this.rotationAxisZ
        };
    }

    static parse() { }

    clone() { }

    getPartName(session: SessionService) {
        const item = session.engine?.objects.find(obj => obj.data.id === this.partId);
        if (item) {
            return item.data.name;
        }
        return '';
    }

}

export interface GroupItemPart {
    id: string;
    faces: string[];
}

export class LoadsGroupItem {

    public groupEditStack: {
        force?: ArrowHelper,
        moment?: ArrowHelper,
        controlMass: { sphere?: Object3D, drag?: DragControls, annotation?: CSS2DObject },
        resources: { lines?: Line2[], materials?: LineMaterial[] }
    } | undefined = undefined;

    constructor(
        public id: string,
        public parts: GroupItemPart[] = [],
        public type: LoadType = LoadType.FORCE,
        // FORCE
        public remotePoint: number[] = [0, 0, 0], // XYZ
        public centerOfMass: number[] = [0, 0, 0], // XYZ
        public forceComponent: number[] = [0, -1, 0], // XYZ
        public momentComponent: number[] = [0, 0, 0], // XYZ 
        // PRESSURE
        public pressureMagnitude: number = 0.1,
        // ACCELERATION
        public nonStructuralMass: number = 0
    ) {

    }

    static fromJSON(data: any) {
        return new LoadsGroupItem(
            data.id,
            data.parts,
            data.type,
            data.remotePoint,
            data.centerOfMass,
            data.forceComponent,
            data.momentComponent,
            data.pressureMagnitude,
            data.nonStructuralMass
        )
    }

    toJSON() {
        return {
            id: this.id,
            parts: this.parts,
            type: this.type,
            // FORCE
            centerOfMass: this.centerOfMass,
            remotePoint: this.remotePoint, // XYZ
            forceComponent: this.forceComponent, // XYZ
            momentComponent: this.momentComponent, // XYZ 
            // PRESSURE
            pressureMagnitude: this.pressureMagnitude,
            // ACCELERATION
            nonStructuralMass: this.nonStructuralMass
        }
    }

    static parse() { }

    clone() { }

    list(session: SessionService) {
        const parts: { name: string, partId: string }[] = [];
        this.parts.forEach(part => {
            const item = session.engine?.objects.find(obj => obj.data.id === part.id);
            if (item) {
                parts.push({
                    name: item.data.name,
                    partId: item.data.id
                });
            }
        });
        return parts;
    }

}


export class LoadCaseItem {

    constructor(
        public id: string,
        public name?: string,
        public type: LoadCaseType = LoadCaseType.STATIC,
        public weightingFactor: number = 1,
        public boundaryConditions: BoundaryConditionItem[] = [],
        public loads: LoadItem[] = [],
        public acceleration: AccelerationItem = { accelerationDirectionVector: [0, 0, -1], accelerationGravity: 1 },
        public stressConstraint: number = 100,
        public minimumDeformationMagnitude: number = 0,
        public maximumDeformationMagnitude: number = 0,
        public deformationPoints: { x: number, y: number, z: number, c: number }[] = [],
        public deformationGlobal = true,
        public groups: LoadsGroupItem[] = [],
        public damping: number = 2,
        public frequencyTable: FrequencyTableEntry[] = [],
        public plotType: 'Log' | 'Linear' = 'Log',
        public baseParts: string[] = [],
        public samplingType: 'Even' | 'EigenValue' = 'Even',
        public baseFreqNumber: number = 20
    ) {

    }

    static parse() { }

    clone() { }

    static fromJSON(data: any) {
        const loadcaseItem = new LoadCaseItem(
            data.id,
            data.name,
            data.type,
            data.weightingFactor,
            [],
            [],
            data.acceleration,
            data.stressConstraint,
            data.minimumDeformationMagnitude,
            data.maximumDeformationMagnitude,
            data.deformationPoints,
            data.deformationGlobal,
            [],
            data.damping ?? 2,
            data.frequencyTable ?? data.psdTable ?? [],
            data.plotType ?? 'Log',
            data.baseParts ?? [],
            data.samplingType ?? 'Even',
            data.baseFreqNumber ?? 20
        );
        if (!data.boundaryConditions) data.boundaryConditions = [];
        if (!data.loads) data.loads = [];
        if (!data.groups) data.groups = [];
        loadcaseItem.boundaryConditions = data.boundaryConditions.map((item: any) => BoundaryConditionItem.fromJSON(item));
        loadcaseItem.loads = data.loads.map((item: any) => LoadItem.fromJSON(item));
        loadcaseItem.groups = data.groups.map((item: any) => LoadsGroupItem.fromJSON(item));
        return loadcaseItem;
    }

    toJSON() {
        return JSON.parse(
            JSON.stringify({
                id: this.id,
                name: this.name,
                type: this.type,
                boundaryConditions: this.boundaryConditions.map(item => item.toJSON()),
                loads: this.loads.map(item => item.toJSON()),
                weightingFactor: this.weightingFactor,
                acceleration: this.acceleration,
                stressConstraint: this.stressConstraint,
                minimumDeformationMagnitude: this.minimumDeformationMagnitude,
                maximumDeformationMagnitude: this.maximumDeformationMagnitude,
                deformationPoints: this.deformationPoints,
                deformationGlobal: this.deformationGlobal,
                groups: this.groups.map(item => item.toJSON()),
                damping: this.damping,
                frequencyTable: this.frequencyTable,
                plotType: this.plotType,
                baseParts: this.baseParts,
                samplingType: this.samplingType,
                baseFreqNumber: this.baseFreqNumber
            })
        );
    }

}